import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ArticleIcon from '@mui/icons-material/Article';
import WavesIcon from '@mui/icons-material/Waves';
import ReportIcon from '@mui/icons-material/Report';
import GroupIcon from '@mui/icons-material/Group';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';
import KpiCard from '../components/dashboard/KpiCard';
import PendingReportsList from '../components/dashboard/PendingReportsList';
import HiddenPostsList from '../components/dashboard/HiddenPostsList';
import DailyTrendsChart from '../components/dashboard/DailyTrendsChart';
import PostTypeDistributionChart from '../components/dashboard/PostTypeDistributionChart';
import TopSpeciesChart from '../components/dashboard/TopSpeciesChart';

interface DashboardSummary {
  kpi: {
    newUsersToday: number;
    activeUsers7d: number;
    postsToday: number;
    reportsToday: number;
    pendingReports: number;
    totalUsers: number;
  };
  pendingReports: Array<{
    id: number;
    type: 'POST' | 'COMMENT' | 'USER';
    reason: string;
    createdAt: string;
    reporterName: string;
  }>;
  hiddenPosts: Array<{
    id: number;
    description: string;
    hiddenAt: string | null;
    hiddenReason: string | null;
    authorName: string;
  }>;
  trends: {
    daily: Array<{ date: string; newUsers: number; newPosts: number }>;
    postTypeDistribution: { NORMAL: number; JOURNAL: number; REPORT: number };
  };
  topSpecies: Array<{ species: string; count: number }>;
}

/// 관리자 대시보드 — SNS 특성에 맞춰 4개 섹션으로 재구성.
/// 1) KPI 카드 6개
/// 2) 커뮤니티 헬스 (대기 신고 · 숨김 게시물)
/// 3) 콘텐츠 트렌드 (일별 라인 · 타입 파이)
/// 4) 낚시 인사이트 (인기 어종 TOP 10)
const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('accessToken');
      const r = await axios.get<DashboardSummary>(`${API_BASE_URL}/admin/dashboard/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (err: any) {
      setError(err.response?.data?.message || '대시보드를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">대시보드</Typography>
        <Tooltip title="새로고침">
          <IconButton onClick={load} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <Stack spacing={4}>
          {/* 섹션 1: KPI 카드 6개 */}
          <Box>
            <Typography variant="overline" color="text.secondary">
              오늘의 요약
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
              <KpiCard
                title="오늘 신규 유저"
                value={data.kpi.newUsersToday}
                icon={<PersonAddIcon />}
                color="primary"
                navigateTo="/users"
              />
              <KpiCard
                title="활성 유저 (7일)"
                value={data.kpi.activeUsers7d}
                icon={<GroupIcon />}
                color="success"
                navigateTo="/users"
              />
              <KpiCard
                title="오늘 게시물"
                value={data.kpi.postsToday}
                icon={<ArticleIcon />}
                color="info"
                navigateTo="/posts"
              />
              <KpiCard
                title="오늘 조황 리포트"
                value={data.kpi.reportsToday}
                icon={<WavesIcon />}
                color="secondary"
                navigateTo="/posts"
              />
              <KpiCard
                title="대기 중인 신고"
                value={data.kpi.pendingReports}
                icon={<ReportIcon />}
                color="error"
                navigateTo="/reports?status=PENDING"
                urgent={data.kpi.pendingReports > 0}
              />
              <KpiCard
                title="총 유저 수"
                value={data.kpi.totalUsers}
                icon={<PeopleIcon />}
                color="warning"
                navigateTo="/users"
              />
            </Box>
          </Box>

          <Divider />

          {/* 섹션 2: 커뮤니티 헬스 */}
          <Box>
            <Typography variant="overline" color="text.secondary">
              커뮤니티 헬스 — 즉시 확인 필요
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
              <PendingReportsList items={data.pendingReports} />
              <HiddenPostsList items={data.hiddenPosts} />
            </Box>
          </Box>

          <Divider />

          {/* 섹션 3: 콘텐츠 트렌드 */}
          <Box>
            <Typography variant="overline" color="text.secondary">
              콘텐츠 트렌드
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
              <DailyTrendsChart data={data.trends.daily} />
              <PostTypeDistributionChart data={data.trends.postTypeDistribution} />
            </Box>
          </Box>

          <Divider />

          {/* 섹션 4: 낚시 인사이트 */}
          <Box>
            <Typography variant="overline" color="text.secondary">
              낚시 인사이트
            </Typography>
            <Box sx={{ mt: 1 }}>
              <TopSpeciesChart data={data.topSpecies} />
            </Box>
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default Dashboard;
